import React, { useState, useCallback, useRef, useEffect } from 'react';
import { validateFile } from '../../services/validationSchemas';
import { jobApplicationService } from '../../services/api';
import { compressImage } from '../../utils/compressImage';
import './Steps.css';

// Estados de validación del documento
const VALIDATION_STATUS = {
  IDLE: 'idle',
  UPLOADING: 'uploading',
  VALIDATING: 'validating',
  SUCCESS: 'success',
  ERROR: 'error',
};

// Configuración de documentos en orden
const DOCUMENT_CONFIG = [
  {
    key: 'national_id',
    label: 'Cédula de Identidad',
    icon: '🪪',
    description: 'Toma 2 fotos de tu cédula (frente y reverso)',
    apiType: 'national_id',
    required: true,
    tips: ['Incluye todos los bordes', 'Sin flash directo'],
  },
  {
    key: 'license',
    label: 'Licencia de Conducir',
    icon: '🚗',
    description: 'Toma 2 fotos de tu licencia (frente y reverso)',
    apiType: 'license',
    required: true,
    tips: ['Verifica que no esté vencida', 'Foto frontal completa'],
  },
  {
    key: 'registration',
    label: 'Matrícula del Vehículo',
    icon: '📋',
    description: 'Toma 2 fotos de la matrícula (frente y reverso). Debe estar a tu nombre.',
    apiType: 'registration',
    required: true,
    tips: ['La moto debe estar a tu nombre', 'Incluye todos los bordes', 'Verifica que sea legible'],
  },
];

// Componente para subir una foto individual
const PhotoUpload = ({
  label,
  side,
  file,
  onUpload,
  disabled,
  isActive
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (file && file instanceof File) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  }, [file]);

  const handleFile = useCallback(async (selectedFile) => {
    const validation = validateFile(selectedFile);
    if (!validation.valid) {
      setLocalError(validation.error);
      return;
    }
    setLocalError(null);
    onUpload(side, await compressImage(selectedFile));
  }, [side, onUpload]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFile(droppedFile);
    }
  }, [handleFile, disabled]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleInputChange = useCallback((e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      handleFile(selectedFile);
    }
  }, [handleFile]);

  const removeFile = useCallback(() => {
    onUpload(side, null);
    setLocalError(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [side, onUpload]);

  return (
    <div className={`photo-upload-card ${file ? 'has-file' : ''} ${disabled ? 'disabled' : ''} ${isActive ? 'active' : ''}`}>
      <div className="photo-label">
        <span className="photo-side-icon">{side === 'front' ? '📄' : '🔄'}</span>
        <span>{label}</span>
      </div>

      {!file ? (
        <div
          className={`photo-dropzone ${isDragging ? 'dragging' : ''} ${disabled ? 'disabled' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !disabled && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            onChange={handleInputChange}
            className="file-input-hidden"
            disabled={disabled}
          />
          <div className="photo-dropzone-content">
            <span className="photo-camera-icon">📷</span>
            <span className="photo-dropzone-text">
              {disabled ? 'Completa el paso anterior' : 'Toca para tomar foto'}
            </span>
          </div>
        </div>
      ) : (
        <div className="photo-preview-container">
          <div className="photo-preview-wrapper">
            <img
              src={imagePreview}
              alt={`Preview ${label}`}
              className="photo-preview-img"
            />
            <button
              type="button"
              className="photo-remove-btn"
              onClick={removeFile}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {localError && (
        <div className="photo-error">
          <span>⚠️</span> {localError}
        </div>
      )}
    </div>
  );
};

// Componente principal de documento con 2 fotos
const DocumentUploadCard = ({
  config,
  document,
  nationalId,
  onUpdatePhoto,
  onValidated,
  isLocked,
  isPreviousValidated,
  documentIndex,
}) => {
  const [validationStatus, setValidationStatus] = useState(VALIDATION_STATUS.IDLE);
  const [validationMessage, setValidationMessage] = useState(null);
  const [validationProgress, setValidationProgress] = useState(0);

  const hasFrontPhoto = !!document.front;
  const hasBackPhoto = !!document.back;
  const hasBothPhotos = hasFrontPhoto && hasBackPhoto;
  const isValidated = document.validated;

  const validateDocument = useCallback(async () => {
    if (!document.front || !document.back) return;

    setValidationStatus(VALIDATION_STATUS.VALIDATING);
    setValidationProgress(0);
    setValidationMessage(null);

    // Animación de progreso
    const progressInterval = setInterval(() => {
      setValidationProgress(prev => Math.min(prev + 5, 90));
    }, 150);

    try {
      const tokenResponse = await jobApplicationService.getToken();
      const token = tokenResponse.data?.token ?? tokenResponse.token;

      const response = await jobApplicationService.validateDocuments(
        config.apiType,
        nationalId,
        document.front,
        document.back,
        token
      );

      clearInterval(progressInterval);
      setValidationProgress(100);

      if (response.success && response.data?.valid) {
        setValidationStatus(VALIDATION_STATUS.SUCCESS);
        // La matrícula a nombre de otra persona no bloquea: se revisa manualmente
        const needsReview = response.data.owner_verified === false;
        setValidationMessage({
          type: needsReview ? 'warning' : 'success',
          text: needsReview
            ? 'No pudimos confirmar que la matrícula esté a tu nombre. Puedes continuar; la revisaremos manualmente.'
            : 'Documento validado correctamente',
        });
        onValidated(config.key, true, response.data);
      } else {
        setValidationStatus(VALIDATION_STATUS.ERROR);
        const errorMsg = response.data?.errors?.join('. ') ||
                        response.message ||
                        'Error al validar el documento';
        setValidationMessage({
          type: 'error',
          text: errorMsg,
        });
        onValidated(config.key, false, response.data);
      }
    } catch (error) {
      clearInterval(progressInterval);
      setValidationStatus(VALIDATION_STATUS.ERROR);

      let errorMessage = 'Error al validar el documento. Intenta de nuevo.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        errorMessage = Object.values(error.response.data.errors).flat().join('. ');
      }

      setValidationMessage({
        type: 'error',
        text: errorMessage,
      });
      onValidated(config.key, false, null);
    }
  }, [document.front, document.back, config.apiType, config.key, nationalId, onValidated]);

  const handlePhotoUpload = (side, file) => {
    // Si cambia alguna foto, resetear validación
    if (isValidated || validationStatus !== VALIDATION_STATUS.IDLE) {
      setValidationStatus(VALIDATION_STATUS.IDLE);
      setValidationMessage(null);
      setValidationProgress(0);
      onValidated(config.key, false, null);
    }
    onUpdatePhoto(config.key, side, file);
  };
    // eslint-disable-next-line
  const retryValidation = () => {
    setValidationStatus(VALIDATION_STATUS.IDLE);
    setValidationMessage(null);
    setValidationProgress(0);
    validateDocument();
  };

  const getCardClassName = () => {
    let classes = 'document-upload-card-v2';
    if (isLocked) classes += ' locked';
    if (isValidated) classes += ' validated';
    if (validationStatus === VALIDATION_STATUS.VALIDATING) classes += ' validating';
    if (validationStatus === VALIDATION_STATUS.ERROR) classes += ' error';
    return classes;
  };

    // Cuando se suben ambas fotos, validar automáticamente
    useEffect(() => {
        if (hasBothPhotos && !isValidated && validationStatus === VALIDATION_STATUS.IDLE) {
            validateDocument();
        }
    }, [hasBothPhotos, isValidated, validationStatus, validateDocument]);


    return (
    <div className={getCardClassName()}>
      {/* Header del documento */}
      <div className="document-header-v2">
        <div className="document-icon-container">
          <span className="document-main-icon">{config.icon}</span>
          {isValidated && (
            <span className="document-validated-badge">✓</span>
          )}
          {isLocked && !isValidated && (
            <span className="document-locked-badge">🔒</span>
          )}
        </div>
        <div className="document-info-v2">
          <h4>
            {config.label}
            {config.required && <span className="required-star">*</span>}
          </h4>
          <p>{config.description}</p>
        </div>
        {/* Indicador de paso */}
        <div className={`document-step-indicator ${isValidated ? 'completed' : isPreviousValidated ? 'current' : 'pending'}`}>
          {isValidated ? '✓' : documentIndex + 1}
        </div>
      </div>

      {/* Mensaje de bloqueo */}
      {isLocked && (
        <div className="document-locked-message">
          <span className="lock-icon">🔒</span>
          <span>Primero valida el documento anterior</span>
        </div>
      )}

      {/* Fotos del documento */}
      {!isLocked && (
        <div className="document-photos-grid">
          <PhotoUpload
            label="Foto Frontal"
            side="front"
            file={document.front}
            onUpload={handlePhotoUpload}
            disabled={false}
            isActive={!hasFrontPhoto}
          />
          <PhotoUpload
            label="Foto Reverso"
            side="back"
            file={document.back}
            onUpload={handlePhotoUpload}
            disabled={!hasFrontPhoto}
            isActive={hasFrontPhoto && !hasBackPhoto}
          />
        </div>
      )}

      {/* Animación de validación */}
      {validationStatus === VALIDATION_STATUS.VALIDATING && (
        <div className="validation-animation">
          <div className="validation-spinner-container">
            <div className="validation-spinner"></div>
            <span className="validation-icon-center">🔍</span>
          </div>
          <div className="validation-progress-bar">
            <div
              className="validation-progress-fill"
              style={{ width: `${validationProgress}%` }}
            ></div>
          </div>
          <span className="validation-text">Validando documento con OCR...</span>
        </div>
      )}

      {/* Mensaje de resultado */}
      {validationMessage && validationStatus !== VALIDATION_STATUS.VALIDATING && (
        <div className={`validation-result ${validationMessage.type}`}>
          <span className="validation-result-icon">
            {{ success: '✅', warning: '⚠️' }[validationMessage.type] || '❌'}
          </span>
          <span className="validation-result-text">{validationMessage.text}</span>
        </div>
      )}

      {/* Éxito animado */}
      {isValidated && validationStatus === VALIDATION_STATUS.SUCCESS && validationMessage?.type !== 'warning' && (
        <div className="validation-success-banner">
          <span className="success-check-animated">✓</span>
          <span>Documento verificado correctamente</span>
        </div>
      )}

      {/* Tips */}
      {!isLocked && !isValidated && !hasBothPhotos && config.tips.length > 0 && (
        <div className="document-tips-v2">
          {config.tips.map((tip, index) => (
            <span key={index} className="tip-item-v2">
              💡 {tip}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const StepDocuments = ({ formData, onUpdateDocuments, onSetDocumentValidated, onNext, onBack }) => {
  const { documents, national_id } = formData;
  const [showTips, setShowTips] = useState(true);

  const handleUpdatePhoto = useCallback((documentType, side, file) => {
    onUpdateDocuments(documentType, side, file);
  }, [onUpdateDocuments]);

  const handleValidated = useCallback((documentType, validated, validationData) => {
    onSetDocumentValidated(documentType, validated, validationData);
  }, [onSetDocumentValidated]);

  // Verificar si se puede continuar (los 3 documentos deben estar validados)
  const canContinue = documents.national_id.validated &&
                      documents.license.validated &&
                      documents.registration.validated;

  // Calcular qué documentos están bloqueados
  const isDocumentLocked = (index) => {
    if (index === 0) return false; // Cédula nunca bloqueada
    if (index === 1) return !documents.national_id.validated; // Licencia bloqueada si cédula no validada
    if (index === 2) return !documents.license.validated; // Matrícula bloqueada si licencia no validada
    return true;
  };

  const isPreviousValidated = (index) => {
    if (index === 0) return true;
    if (index === 1) return documents.national_id.validated;
    if (index === 2) return documents.license.validated;
    return false;
  };

  return (
    <div className="step-container step-documents-v2">
      {/* Header */}
      <div className="step-header">
        <div className="step-icon-animated">
          <span className="main-icon">📸</span>
          <span className="sparkle sparkle-1">✨</span>
          <span className="sparkle sparkle-2">✨</span>
        </div>
        <h2 className="step-title">Valida tus documentos</h2>
        <p className="step-subtitle">
          Sube 2 fotos de cada documento (frente y reverso) para validar tu identidad
        </p>
      </div>

      {/* Alerta si no hay national_id */}
      {!national_id && (
        <div className="alert-box warning">
          <span className="alert-icon">⚠️</span>
          <div className="alert-content">
            <strong>Atención</strong>
            <p>No se encontró tu número de cédula. Vuelve al paso anterior.</p>
          </div>
        </div>
      )}

      {/* Tips generales */}
      {showTips && (
        <div className="tips-banner">
          <div className="tips-header">
            <span className="tips-icon">💡</span>
            <span className="tips-title">Consejos para mejores fotos</span>
            <button
              type="button"
              className="tips-close"
              onClick={() => setShowTips(false)}
            >
              ✕
            </button>
          </div>
          <div className="tips-list">
            <div className="tip-card">
              <span className="tip-emoji">☀️</span>
              <span>Buena luz</span>
            </div>
            <div className="tip-card">
              <span className="tip-emoji">📐</span>
              <span>Completo</span>
            </div>
            <div className="tip-card">
              <span className="tip-emoji">🔍</span>
              <span>Legible</span>
            </div>
            <div className="tip-card">
              <span className="tip-emoji">🚫</span>
              <span>Sin reflejos</span>
            </div>
          </div>
        </div>
      )}

      {/* Flujo de documentos */}
      <div className="documents-flow">
        {DOCUMENT_CONFIG.map((config, index) => (
          <DocumentUploadCard
            key={config.key}
            config={config}
            document={documents[config.key]}
            nationalId={national_id}
            onUpdatePhoto={handleUpdatePhoto}
            onValidated={handleValidated}
            isLocked={isDocumentLocked(index)}
            isPreviousValidated={isPreviousValidated(index)}
            documentIndex={index}
          />
        ))}
      </div>

      {/* Nota de seguridad */}
      <div className="security-note">
        <span className="security-icon">🔒</span>
        <div className="security-text">
          <strong>Tus datos están seguros</strong>
          <p>
              Usamos encriptación y validación OCR para verificar
              tus documentos de forma segura.
          </p>
        </div>
      </div>

      {/* Acciones */}
      <div className="step-actions">
        <button type="button" className="btn-secondary" onClick={onBack}>
          <span className="btn-icon">←</span>
          Atrás
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={onNext}
          disabled={!canContinue}
        >
          {canContinue ? 'Continuar' : 'Valida los 3 documentos'}
          <span className="btn-icon">→</span>
        </button>
      </div>

      {/* Indicador de progreso de documentos */}
      <div className="documents-progress-v2">
        {DOCUMENT_CONFIG.map((config, index) => (
          <React.Fragment key={config.key}>
            <div className="progress-item-v2">
              <span className={`progress-dot-v2 ${
                documents[config.key].validated ? 'completed' :
                isPreviousValidated(index) && !isDocumentLocked(index) ? 'current' :
                !config.required ? 'optional' : 'pending'
              }`}>
                {documents[config.key].validated ? '✓' : index + 1}
              </span>
              <span className="progress-label-v2">{config.label.split(' ')[0]}</span>
            </div>
            {index < DOCUMENT_CONFIG.length - 1 && (
              <div className={`progress-line-v2 ${documents[config.key].validated ? 'completed' : ''}`}></div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default StepDocuments;
